import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Input, Spin, Popconfirm } from 'antd';
import {
    ShoppingCartOutlined,
    MessageOutlined,
    CloseCircleOutlined,
    CreditCardOutlined,
    SendOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { toastr } from 'react-redux-toastr';

import { profileSelectors, fetchOrders } from '../../../features/profile';
import { ordersApi, ordersSelectors } from '../../../features/orders';

const { TextArea } = Input;

const statusStyles = {
    'Выполнен': { background: 'rgba(82, 196, 26, 0.15)', color: '#52c41a', border: '1px solid rgba(82, 196, 26, 0.3)' },
    'Обработка': { background: 'rgba(24, 144, 255, 0.15)', color: '#1890ff', border: '1px solid rgba(24, 144, 255, 0.3)' },
    'Ожидается оплата': { background: 'rgba(250, 173, 20, 0.15)', color: '#faad14', border: '1px solid rgba(250, 173, 20, 0.3)' },
    'На удержании': { background: 'rgba(250, 173, 20, 0.1)', color: '#d4a017', border: '1px solid rgba(212, 160, 23, 0.3)' },
    'Отменён': { background: 'rgba(208, 2, 2, 0.15)', color: '#ff4d4f', border: '1px solid rgba(208, 2, 2, 0.3)' },
    'Возврат': { background: 'rgba(255, 77, 79, 0.1)', color: '#ff7a45', border: '1px solid rgba(255, 122, 69, 0.3)' },
};

const defaultStatusStyle = { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.15)' };

const cancellableStatuses = ['Ожидается оплата', 'На удержании', 'Обработка'];
const payableStatuses = ['Ожидается оплата'];

const ProfileOrders = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const orders = useSelector(profileSelectors.selectOrders);
    const isLoading = useSelector(profileSelectors.selectOrdersLoading);
    const pagination = useSelector(profileSelectors.selectOrdersPagination);
    const token = useSelector((state) => state.auth?.token);
    const paymentUrls = useSelector((state) => state.orders?.paymentUrls || {});

    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [notesOrderId, setNotesOrderId] = useState(null);
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [cancellingId, setCancellingId] = useState(null);

    useEffect(() => {
        dispatch(fetchOrders(1));
    }, [dispatch]);

    const handlePageChange = (page) => {
        dispatch(fetchOrders(page));
    };

    const getOrderId = (record) => {
        return record.id || record.order_id || String(record['Заказ']).replace(/\D/g, '');
    };

    const openNotes = async (record) => {
        const orderId = getOrderId(record);
        setNotesOrderId(orderId);
        setNotesModalOpen(true);
        setNotes([]);
        setNewNote('');
        setNotesLoading(true);

        try {
            const result = await ordersApi.fetchOrderNotes(orderId);
            setNotes(Array.isArray(result) ? result : []);
        } catch (error) {
            console.error('Fetch notes error:', error);
            toastr.error('Ошибка', 'Не удалось загрузить заметки');
        } finally {
            setNotesLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) return;

        setAddingNote(true);
        try {
            await ordersApi.addOrderNote(notesOrderId, newNote.trim());
            setNewNote('');
            const result = await ordersApi.fetchOrderNotes(notesOrderId);
            setNotes(Array.isArray(result) ? result : []);
            toastr.success('Успешно', 'Заметка добавлена');
        } catch (error) {
            console.error('Add note error:', error);
            toastr.error('Ошибка', 'Не удалось добавить заметку');
        } finally {
            setAddingNote(false);
        }
    };

    const handleCancel = async (record) => {
        const orderId = getOrderId(record);
        setCancellingId(orderId);

        try {
            await ordersApi.cancelOrder(orderId, token);
            toastr.success('Успешно', 'Заказ отменён');
            dispatch(fetchOrders(pagination.page));
        } catch (error) {
            console.error('Cancel order error:', error);
            toastr.error('Ошибка', 'Не удалось отменить заказ');
        } finally {
            setCancellingId(null);
        }
    };

    const getPaymentUrl = (orderId) => {
        const entry = paymentUrls[orderId];
        if (!entry) return null;
        const TTL = 20 * 60 * 1000;
        if (Date.now() - entry.createdAt > TTL) return null;
        return entry.url;
    };

    const handlePay = (record) => {
        const orderId = getOrderId(record);
        const url = getPaymentUrl(orderId);
        if (url) {
            window.open(url, '_blank');
        } else {
            navigate(`/order-success/${orderId}`);
        }
    };

    const columns = [
        {
            title: 'Заказ',
            dataIndex: 'Заказ',
            key: 'order',
            width: 120,
            render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
        },
        {
            title: 'Дата',
            dataIndex: 'Дата',
            key: 'date',
            width: 110,
        },
        {
            title: 'Статус',
            dataIndex: 'Статус',
            key: 'status',
            width: 150,
            render: (text) => {
                const style = statusStyles[text] || defaultStatusStyle;
                return (
                    <span className="order-status-tag" style={style}>
                        {text}
                    </span>
                );
            },
        },
        {
            title: 'Итого',
            dataIndex: 'Итого',
            key: 'total',
            width: 100,
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 250,
            render: (_, record) => {
                const status = record['Статус'];
                const orderId = getOrderId(record);
                const canCancel = cancellableStatuses.includes(status);
                const canPay = payableStatuses.includes(status) && !!getPaymentUrl(orderId);

                return (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button
                            className="order-action-btn"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => navigate(`/order-detail/${orderId}`)}
                        >
                            Подробнее
                        </Button>
                        <Button
                            className="order-action-btn"
                            size="small"
                            icon={<MessageOutlined />}
                            onClick={() => openNotes(record)}
                        >
                            Заметки
                        </Button>
                        {canPay && (
                            <Button
                                className="order-action-btn-pay"
                                size="small"
                                icon={<CreditCardOutlined />}
                                onClick={() => handlePay(record)}
                            >
                                Оплатить
                            </Button>
                        )}
                        {canCancel && (
                            <Popconfirm
                                title="Отменить заказ?"
                                description="Это действие нельзя отменить"
                                onConfirm={() => handleCancel(record)}
                                okText="Да, отменить"
                                cancelText="Нет"
                                overlayClassName="dark-popconfirm"
                            >
                                <Button
                                    className="order-action-btn-cancel"
                                    size="small"
                                    icon={<CloseCircleOutlined />}
                                    loading={cancellingId === orderId}
                                >
                                    Отменить
                                </Button>
                            </Popconfirm>
                        )}
                    </div>
                );
            },
        },
    ];

    if (isLoading) {
        return (
            <div className="profile-orders-table orders-skeleton">
                <div className="orders-skeleton-header">
                    <div className="skeleton-shimmer" style={{ width: 60, height: 14 }} />
                    <div className="skeleton-shimmer" style={{ width: 40, height: 14 }} />
                    <div className="skeleton-shimmer" style={{ width: 56, height: 14 }} />
                    <div className="skeleton-shimmer" style={{ width: 46, height: 14 }} />
                    <div className="skeleton-shimmer" style={{ width: 72, height: 14 }} />
                </div>
                {[0, 1, 2, 3, 4].map((i) => (
                    <div
                        className="orders-skeleton-row"
                        key={i}
                        style={{ animationDelay: `${i * 40}ms` }}
                    >
                        <div className="skeleton-shimmer" style={{ width: 72, height: 16 }} />
                        <div className="skeleton-shimmer" style={{ width: 80, height: 16 }} />
                        <div className="skeleton-shimmer" style={{ width: 90, height: 24, borderRadius: 4 }} />
                        <div className="skeleton-shimmer" style={{ width: 64, height: 16 }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <div className="skeleton-shimmer" style={{ width: 80, height: 28, borderRadius: 6 }} />
                            <div className="skeleton-shimmer" style={{ width: 80, height: 28, borderRadius: 6 }} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (!orders || orders.length === 0) {
        return (
            <div className="profile-empty-state">
                <ShoppingCartOutlined className="profile-empty-icon" />
                <p className="profile-empty-title">У вас пока нет заказов</p>
                <p className="profile-empty-description">Здесь будут отображаться ваши заказы товаров</p>
            </div>
        );
    }

    return (
        <div className="profile-orders-table">
            <Table
                columns={columns}
                dataSource={orders}
                rowKey={(record) => record['Заказ']}
                pagination={{
                    current: pagination.page,
                    total: pagination.total,
                    pageSize: pagination.perPage,
                    showSizeChanger: false,
                    onChange: handlePageChange,
                }}
                scroll={{ x: 700 }}
            />

            <Modal
                title={`Заметки к заказу #${notesOrderId}`}
                open={notesModalOpen}
                onCancel={() => setNotesModalOpen(false)}
                footer={null}
                width={500}
                className="notes-modal"
            >
                {notesLoading ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                        <Spin />
                    </div>
                ) : (
                    <>
                        <div className="notes-list">
                            {notes.length === 0 ? (
                                <div className="notes-empty">Нет заметок</div>
                            ) : (
                                notes.map((item, idx) => (
                                    <div className="note-item" key={item.id || idx}>
                                        <div
                                            className="note-text"
                                            dangerouslySetInnerHTML={{ __html: item.note }}
                                        />
                                        <div className="note-meta">
                                            {item.date_created
                                                ? new Date(item.date_created).toLocaleString('ru-RU')
                                                : ''}
                                            {item.author ? ` — ${item.author}` : ''}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="notes-input-row">
                            <TextArea
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                                placeholder="Написать заметку..."
                                autoSize={{ minRows: 2, maxRows: 4 }}
                                style={{ flex: 1 }}
                            />
                            <Button
                                className="notes-send-btn"
                                icon={<SendOutlined />}
                                onClick={handleAddNote}
                                loading={addingNote}
                                disabled={!newNote.trim()}
                            >
                                Отправить
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default ProfileOrders;
